import type { HandRaisedUser, UserId } from 'features/users/types';
import type { ActionNotification } from 'features/notifications/types';
import type { Action } from '@reduxjs/toolkit';
import type { SidePanelType } from 'features/side-panels/types';

import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { call, put, select } from 'redux-saga/effects';
import { selectAllHandRaisedForNotification } from 'features/users/selectors/selectAllHandRaisedForNotification';
import { selectHandRaisedNotification } from 'features/notifications/notificationsSlice';
import { HAND_RAISED_NOTIFICATION_ID } from 'features/notifications/constants';
import { closeActionNotification } from 'features/notifications/thunks/closeActionNotification';
import { selectSidePanel, sidePanelOpened } from 'features/side-panels/sidePanelsSlice';
import { addActionNotification } from 'features/notifications/action-notifications/addActionNotification';
import i18n from 'i18n';
import { updateActionNotification } from 'features/notifications/action-notifications/updateActionNotification';
import { selectLocalUserId } from 'features/users/usersSlice';
import { raiseHandNotificationUpdateRequested } from 'features/notifications/actions';
import { isParticipantsPanelAccessible } from 'features/side-panels/sagas/filterAccessiblePanels';

export function* handleRaiseHandNotificationSaga(
  action: ReturnType<typeof raiseHandNotificationUpdateRequested>
) {
  try {
    const canAccessParticipantsPanel: boolean = yield call(isParticipantsPanelAccessible);
    if (!canAccessParticipantsPanel) {
      return;
    }

    const users: HandRaisedUser[] = yield select(selectAllHandRaisedForNotification);
    const localUserId: UserId = yield select(selectLocalUserId);

    // skip local user
    const handRaisedUsers = users.filter((user) => user.id !== localUserId);

    const handRaisedUsersCount = handRaisedUsers.length;

    const activeNotification: ActionNotification = yield select(selectHandRaisedNotification);

    if (handRaisedUsersCount === 0 && activeNotification) {
      // close the notification if the last user lowered hand
      const cleanupNotificationThunk: Action = yield call(
        closeActionNotification,
        activeNotification.id
      );
      yield put(cleanupNotificationThunk);

      return;
    }

    if (handRaisedUsersCount === 0) {
      // there are no users with the raised hand, no need to create or update the notification
      return;
    }

    const featuredUser = handRaisedUsers[0];
    if (!featuredUser) {
      // no user to be displayed on the notification
      return;
    }

    if (!activeNotification) {
      // show the notification only for the hand-raised requests
      if (action.payload.reason !== 'hand-raised') {
        return;
      }

      const activePanel: SidePanelType = yield select(selectSidePanel);

      const actionNotificationThunk: Action = yield call(
        addActionNotification,
        {
          content: i18n.t('notifications:hand_raised.content', {
            username: featuredUser.name,
            count: handRaisedUsersCount - 1,
            context: handRaisedUsersCount > 1 ? 'grouped' : undefined,
          }),
          okText: i18n.t('notifications:hand_raised.btn_ok_label'),
          okAction: sidePanelOpened('users'),
          hideOkAction: activePanel === 'users',
          initiatorId: featuredUser.id,
          userAgnostic: true,
          preventCloseOnAction: true,
        },
        {
          toastId: `${HAND_RAISED_NOTIFICATION_ID}_${Date.now()}`,
        }
      );
      yield put(actionNotificationThunk);

      return;
    }

    // group incoming notifications
    const updateNotificationThunk: Action = yield call(
      updateActionNotification,
      activeNotification.id,
      {
        content: i18n.t('notifications:hand_raised.content', {
          username: featuredUser.name,
          count: handRaisedUsersCount - 1,
          context: handRaisedUsersCount > 1 ? 'grouped' : undefined,
        }),
        initiatorId: featuredUser.id,
      }
    );
    yield put(updateNotificationThunk);
  } catch (error) {
    Sentry.captureException(error);
    logger.remote({ tier: 1 }).error('Cannot handle raise hand notification', error);
  }
}
